'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

// ── Mock data — all three columns are demo until wired ─────────────────────

interface ScheduleRow {
  time: string;
  jobId: string;
  address: string;
  crew: string;
  stage: 'Scheduled' | 'En Route' | 'In Progress' | 'Punch List' | 'Complete';
}

const MOCK_SCHEDULE: ScheduleRow[] = [];

interface CrewRow {
  name: string;
  status: 'Clocked In' | 'En Route' | 'On Site' | 'Cleared';
  assignment: string;
  lastUpdate: string;
}

const MOCK_CREWS: CrewRow[] = [];

// ── Helpers ────────────────────────────────────────────────────────────────

const stageColor: Record<ScheduleRow['stage'], { bg: string; fg: string }> = {
  Scheduled:    { bg: 'rgba(7,176,233,0.10)',  fg: '#07B0E9' },
  'En Route':   { bg: 'rgba(245,158,11,0.10)', fg: '#F59E0B' },
  'In Progress':{ bg: 'rgba(3,52,148,0.10)',   fg: '#033494' },
  'Punch List': { bg: 'rgba(139,92,246,0.10)', fg: '#8B5CF6' },
  Complete:     { bg: 'rgba(16,185,129,0.10)', fg: '#10B981' },
};

const crewStatusColor: Record<CrewRow['status'], { bg: string; fg: string; dot: string }> = {
  'Clocked In': { bg: 'rgba(7,176,233,0.10)',  fg: '#07B0E9', dot: '#07B0E9' },
  'En Route':   { bg: 'rgba(245,158,11,0.10)', fg: '#F59E0B', dot: '#F59E0B' },
  'On Site':    { bg: 'rgba(16,185,129,0.10)', fg: '#10B981', dot: '#10B981' },
  'Cleared':    { bg: 'rgba(107,114,128,0.10)', fg: '#6B7280', dot: '#9CA3AF' },
};

// ── Component ──────────────────────────────────────────────────────────────

export default function Row2LiveOps() {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* ── Today's Schedule ────────────────────────────────────────────── */}
      <div
        className="relative rounded-xl overflow-hidden"
        style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
      >
        <DemoDataPill />
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="CalendarDaysIcon" size={15} style={{ color: '#07B0E9' }} />
            <h3
              className="text-base font-bold"
              style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
            >
              Today's Schedule
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{MOCK_SCHEDULE.length} jobs · sorted by start time</p>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="text-left text-gray-500 uppercase tracking-wider">
                <th className="px-3 py-2 font-semibold">Time</th>
                <th className="px-3 py-2 font-semibold">Job</th>
                <th className="px-3 py-2 font-semibold">Address</th>
                <th className="px-3 py-2 font-semibold">Crew</th>
                <th className="px-3 py-2 font-semibold">Stage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {MOCK_SCHEDULE.map((row) => {
                const c = stageColor[row.stage];
                return (
                  <tr key={row.jobId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2.5 text-gray-700 font-medium whitespace-nowrap">{row.time}</td>
                    <td className="px-3 py-2.5 text-gray-700 font-mono">{row.jobId}</td>
                    <td className="px-3 py-2.5 text-gray-600">{row.address}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{row.crew}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap"
                        style={{ background: c.bg, color: c.fg }}
                      >
                        {row.stage}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50">
          <a
            href="/interactive-calendar-schedule-management"
            className="text-xs font-semibold flex items-center gap-1"
            style={{ color: '#07B0E9' }}
          >
            View full schedule
            <Icon name="ArrowRightIcon" size={11} />
          </a>
        </div>
      </div>

      {/* ── Crew Dispatch Status ────────────────────────────────────────── */}
      <div
        className="relative rounded-xl p-5"
        style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
      >
        <DemoDataPill />
        <div className="flex items-center gap-2 mb-3">
          <Icon name="WrenchScrewdriverIcon" size={15} style={{ color: '#033494' }} />
          <h3
            className="text-base font-bold"
            style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
          >
            Crew Dispatch Status
          </h3>
        </div>
        <p className="text-xs text-gray-400 mb-3">{MOCK_CREWS.length} active crews</p>
        <div className="space-y-2">
          {MOCK_CREWS.map((c) => {
            const cs = crewStatusColor[c.status];
            return (
              <div
                key={c.name}
                className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors"
                style={{ border: '1px solid #F3F4F6' }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cs.dot }} />
                  <p className="text-sm font-semibold text-gray-800 truncate">{c.name}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                    style={{ background: cs.bg, color: cs.fg }}
                  >
                    {c.status}
                  </span>
                  <span className="text-[11px] text-gray-500 font-mono w-16 text-right">{c.assignment}</span>
                  <span className="text-[10px] text-gray-400 w-16 text-right">{c.lastUpdate}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Money Today ─────────────────────────────────────────────────── */}
      <div
        className="relative rounded-xl p-5"
        style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
      >
        <DemoDataPill />
        <div className="flex items-center gap-2 mb-3">
          <Icon name="BanknotesIcon" size={15} style={{ color: '#10B981' }} />
          <h3
            className="text-base font-bold"
            style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
          >
            Money Today
          </h3>
        </div>
        <div className="space-y-3">
          {/* Collected */}
          <div className="rounded-lg p-3" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.18)' }}>
            <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#047857' }}>Collected today</p>
            <p className="text-2xl font-bold mt-0.5" style={{ color: '#065F46', fontFamily: 'Inter, Poppins, sans-serif' }}>$0</p>
            <p className="text-[11px] mt-0.5" style={{ color: '#10B981' }}>—</p>
          </div>
          {/* Invoiced unpaid */}
          <div className="rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)' }}>
            <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#92400E' }}>Invoiced, not yet paid</p>
            <p className="text-2xl font-bold mt-0.5" style={{ color: '#78350F', fontFamily: 'Inter, Poppins, sans-serif' }}>$0 <span className="text-xs font-medium text-gray-500">(0 inv.)</span></p>
            <p className="text-[11px] mt-0.5" style={{ color: '#F59E0B' }}>0 over 30 days</p>
          </div>
          {/* Goal progress */}
          <div>
            <div className="flex items-baseline justify-between">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">Daily goal progress</p>
              <p className="text-xs font-bold" style={{ color: '#000150' }}>0%</p>
            </div>
            <div className="w-full mt-1.5 h-2 rounded-full" style={{ background: '#F3F4F6' }}>
              <div
                className="h-2 rounded-full"
                style={{ width: '0%', background: 'linear-gradient(90deg, #07B0E9 0%, #10B981 100%)' }}
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-1">$0 of $0</p>
          </div>

          {/* Burn-down sparkline vs linear goal trajectory */}
          <RevenueBurnDownSparkline />
        </div>
      </div>
    </section>
  );
}

// ── Revenue Burn-Down sparkline ────────────────────────────────────────────
// 9 hourly snapshots, 8am–4pm (mock). Dashed line is the linear trajectory
// toward the $48k daily goal; solid line is actual collected. Solid colors
// green when at/above trajectory at the latest hour, red when below.
function RevenueBurnDownSparkline() {
  const actual: number[] = [];
  const goal = 0;
  const w = 220;
  const h = 40;

  // Empty/zero state: no snapshots to plot — render the labelled frame only.
  if (actual.length === 0 || goal === 0) {
    return (
      <div className="pt-3 border-t border-gray-100">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
          Burn-down vs goal
        </p>
        <svg
          width="100%"
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className="block"
        />
      </div>
    );
  }

  const trajectory = actual.map((_, i) => (goal * (i + 1)) / actual.length);

  const xFor = (i: number) => (i / (actual.length - 1)) * w;
  const yFor = (v: number) => h - (v / goal) * h;

  const actualPath = actual.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');
  const trajPath = trajectory.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');

  const currentIdx = actual.length - 1;
  const onTrack = actual[currentIdx] >= trajectory[currentIdx];
  const lineColor = onTrack ? '#10B981' : '#EF4444';

  return (
    <div className="pt-3 border-t border-gray-100">
      <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
        Burn-down vs goal
      </p>
      <svg
        width="100%"
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="block"
      >
        <path d={trajPath} stroke="#9CA3AF" strokeWidth="1" strokeDasharray="3 3" fill="none" />
        <path
          d={actualPath}
          stroke={lineColor}
          strokeWidth="1.75"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
